import { CSSProperties, FC, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "classnames";
import { Dialog } from "../../core/Dialog/Dialog";
import { Icon } from "../../core/Icon/Icon";
import { Image } from "../../core/Image/Image";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { PageRoutes } from "../../../constants/routes";
import { useProfiles } from "../../../hooks/useProfiles";
import { useCurrentProfile } from "../../../hooks/useProfileSession";
import { canAddProfile, Profile } from "../../../models/profile";
import { useProfileFlow } from "../../../pages/WhoIsPlaying/useProfileFlow";
import { ProfileFlowDialogs } from "../../../pages/WhoIsPlaying/ProfileFlowDialogs";
import PickerBackground from "../../../pages/WhoIsPlaying/assets/whoPlaying_1x.webp";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { isGamepadMode } from "../../../hooks/useCrossInput";
import { useStyles } from "./ProfileSwitcher.style";

const ProfileAvatar: FC<{ avatar?: string; className: string }> = ({
  avatar,
  className,
}) =>
  avatar ? (
    <Image src={avatar} alt="" className={className} />
  ) : (
    <span className={clsx("icon-bold-user", className)} aria-hidden />
  );

const MENU_WIDTH = 320;
const MENU_GAP = 12;
const SCREEN_EDGE = 16;

interface IProfileSwitcherProps {
  triggerClassName?: string;
  triggerSelectedClassName?: string;
  onOpenChange?: (open: boolean) => void;
}

export const ProfileSwitcher: FC<IProfileSwitcherProps> = ({
  triggerClassName,
  triggerSelectedClassName,
  onOpenChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const store = useProfiles();
  const flow = useProfileFlow(store);
  const { currentProfile } = useCurrentProfile();
  const { setActiveElement } = useGamepadContext();
  const buttonRef = useRef<HTMLSpanElement>(null);
  const [menuPosition, setMenuPosition] = useState<CSSProperties>();

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    new window.Image().src = PickerBackground;
    setMenuPosition({
      top: rect.bottom + MENU_GAP,
      left: Math.min(rect.left, window.innerWidth - MENU_WIDTH - SCREEN_EDGE),
    });
    onOpenChange?.(true);
  };

  // Back only cancels the dialog, so the controller focus is handed back here
  const closeMenu = () => {
    setMenuPosition(undefined);
    onOpenChange?.(false);
    if (isGamepadMode() && buttonRef.current) {
      setActiveElement(buttonRef.current);
    }
  };

  const switchTo = (profile: Profile) => {
    closeMenu();
    if (profile.id === currentProfile?.id) return;
    flow.selectProfile(profile);
  };

  const openPicker = (manage: boolean) => {
    closeMenu();
    navigate(PageRoutes.WhoIsPlaying, {
      state: {
        from: location.pathname + location.search,
        manage,
        focusAdd: !manage,
      },
    });
  };

  return (
    <div>
      <span
        ref={buttonRef}
        className={clsx(classes.button, triggerClassName)}
        data-open={!!menuPosition}
        data-gamepad="menu-profile-switcher"
        data-selected-class={triggerSelectedClassName ?? classes.selected}
        data-button-sound="selection"
        role="button"
        tabIndex={-1}
        aria-haspopup="menu"
        aria-expanded={!!menuPosition}
        aria-label={t("A11y_Switch profile")}
        onClick={openMenu}
      >
        <ProfileAvatar
          avatar={currentProfile?.avatar}
          className={classes.buttonAvatar}
        />
        <span className={classes.caret} />
      </span>

      <Dialog
        open={!!menuPosition}
        hideLayout
        onClose={closeMenu}
        aria-label={t("A11y_Switch profile")}
        className={classes.menu}
        style={menuPosition}
        onClick={(event) => {
          if (event.target === event.currentTarget) closeMenu();
        }}
      >
        <div className={classes.menuContent} role="menu">
          {store.profiles.map((profile, index) => (
            <div
              key={profile.id}
              className={classes.item}
              data-gamepad={
                index === 0
                  ? "default-selected"
                  : `profile-switcher-${profile.id}`
              }
              data-selected-class={classes.itemSelected}
              data-button-sound="selection"
              role="menuitemradio"
              tabIndex={-1}
              aria-checked={profile.id === currentProfile?.id}
              onClick={() => switchTo(profile)}
            >
              <span className={classes.itemAvatarWrapper}>
                <ProfileAvatar
                  avatar={profile.avatar}
                  className={classes.itemAvatar}
                />
                {profile.hasLock && (
                  <span
                    className={classes.lockBadge}
                    data-testid="profile-switcher-lock"
                  >
                    <span
                      className={clsx("icon-bold-lock", classes.lockGlyph)}
                      aria-hidden
                    />
                  </span>
                )}
              </span>
              <Typography
                variant={TypographyVariant.BodyB2Bold}
                className={classes.itemName}
              >
                {profile.name}
              </Typography>
            </div>
          ))}
          {canAddProfile(store.profiles) && (
            <div
              className={classes.item}
              data-gamepad="profile-switcher-add"
              data-selected-class={classes.itemSelected}
              data-button-sound="selection"
              role="menuitem"
              tabIndex={-1}
              onClick={() => openPicker(false)}
            >
              <span className={classes.itemIcon}>
                <Icon name="icon-linear-add" size={24} />
              </span>
              <Typography variant={TypographyVariant.BodyB2Bold}>
                {t("WhoIsPlaying_Add New Profile")}
              </Typography>
            </div>
          )}
          <div
            className={classes.item}
            data-gamepad="profile-switcher-manage"
            data-selected-class={classes.itemSelected}
            data-button-sound="selection"
            role="menuitem"
            tabIndex={-1}
            onClick={() => openPicker(true)}
          >
            <span className={classes.itemIcon}>
              <Icon name="icon-bold-edit-2" size={24} />
            </span>
            <Typography variant={TypographyVariant.BodyB2Bold}>
              {t("WhoIsPlaying_Manage Profiles")}
            </Typography>
          </div>
        </div>
      </Dialog>

      <ProfileFlowDialogs flow={flow} />
    </div>
  );
};
